import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Why study old dividend cuts?',
    a: 'Because the warning signs almost always show up YEARS before the cut. Payout ratios drift above 90%, free cash flow can\'t cover the dividend, debt rises faster than earnings, and the Triangle Score declines. Investors who pay attention to those signals avoid most cuts. Investors who chase the headline yield walk right into them.',
  },
  {
    q: 'Were these cuts predictable?',
    a: 'In hindsight, very. Kraft Heinz showed payout ratio above 100% for two years before the 2019 cut. AT&T\'s FCF coverage of the dividend dropped below 1x in 2021, a year before the 2022 spin-off cut. Walgreens had four years of declining FCF and rising debt before the 2024 cut. Disney suspended in 2020 — but cruise-line and theme-park revenue had been the writing on the wall well before COVID. The signs are visible to anyone who looks.',
  },
  {
    q: 'How does DiviDrip help me spot the next one?',
    a: 'The Dividend Triangle score combines payout ratio, FCF coverage, debt trends, and streak — the same four signals these cuts shared. Names with a falling Triangle Score and a payout ratio creeping above 80% are exactly the pattern these case studies share. The Streak Risk Chart also flags when the dividend per share or coverage starts deteriorating quarter-over-quarter.',
  },
  {
    q: 'Are dividend cuts always bad?',
    a: 'Sometimes a cut is the right move. AT&T cut in 2022 because they spun off Warner Media — the dividend right-sized to the remaining business. The TOTAL shareholder return (including the spin-off shares) wasn\'t catastrophic. But for income investors who needed the payment, the cut still meant a 47% drop in monthly cash flow. The right question is: does the cut signal a permanent decline, or a one-time reset?',
  },
];

export default function LearnDividendCutsDecade() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="Dividend Cuts of the Decade — What 12 Big Cuts Taught Us | DiviDrip"
      description="GE, Wells Fargo, AT&T, Disney, Walgreens, 3M, Intel — the biggest dividend cuts and what the Triangle Score, payout ratio, and FCF coverage flagged before each one. Pattern-recognition you can use today."
      canonical="https://twylightcrow.com/learn/dividend-cuts-of-the-decade"
      guidePath="/learn/dividend-cuts-of-the-decade"
      sectionLabel="Strategy & Psychology"
      sectionAnchor="strategy"
      kicker="Learn · Risk education"
      h1="Dividend Cuts of the Decade — What 12 Big Cuts Taught Us"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/payout-ratio', label: 'The payout ratio — your first dividend safety check' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle score' },
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers I check before buying' },
        { to: '/learn/is-9-percent-yield-safe', label: 'Is a 9%+ yield safe? Honest examples' },
      ]}
    >
      <p>
        Every dividend investor&apos;s nightmare is the cut. The cheque arrives 30%
        smaller. The yield-on-cost halves. The "safe income" thesis evaporates
        overnight. And on the way down, the stock drops 20-40% as forced sellers
        unwind their positions.
      </p>
      <p>
        Here&apos;s the thing: <strong>almost every major dividend cut of the last
        decade was visible months — sometimes years — before it happened.</strong>{' '}
        Twelve case studies, twelve nearly identical warning patterns. Let&apos;s walk
        through them.
      </p>

      <aside className="not-prose my-6 rounded-lg border border-sky-300/40 dark:border-sky-700/40 bg-sky-50/60 dark:bg-sky-950/30 p-4">
        <p className="text-sm font-semibold text-sky-900 dark:text-sky-200 mb-1">
          For ETF investors
        </p>
        <p className="text-sm text-sky-900/85 dark:text-sky-200/85">
          The cut-prediction pattern in this guide (payout ratio + FCF coverage
          + debt + Triangle) applies to single-name STOCKS only. ETF
          distributions can also be cut, but for very different reasons —
          NAV decay, options-roll losses, fund-family restructuring. See{' '}
          <Link to="/learn/evaluating-dividend-etfs" className="underline">
            How to Evaluate Dividend ETFs
          </Link>
          {' '}for the ETF-specific framework.
        </p>
      </aside>

      <h2>The 12 biggest cuts (2008-2024)</h2>
      <table className="learn-table">
        <thead>
          <tr><th>Year</th><th>Stock</th><th>Cut size</th><th>Trigger</th></tr>
        </thead>
        <tbody>
          <tr><td>2008</td><td>Wells Fargo (WFC)</td><td>-85%</td><td>Financial crisis, regulator pressure</td></tr>
          <tr><td>2009</td><td>General Electric (GE)</td><td>-68%</td><td>Recession + GE Capital write-downs</td></tr>
          <tr><td>2017</td><td>General Electric (GE)</td><td>-50%</td><td>Power-segment collapse, payout ratio &gt; 100%</td></tr>
          <tr><td>2018</td><td>General Electric (GE)</td><td>-92%</td><td>Second cut in two years — dividend now $0.01/qtr</td></tr>
          <tr><td>2019</td><td>Kraft Heinz (KHC)</td><td>-36%</td><td>Brand impairments, 100%+ payout for two years</td></tr>
          <tr><td>2020</td><td>Walt Disney (DIS)</td><td>Suspended</td><td>COVID closures wiped out theme park &amp; cruise revenue</td></tr>
          <tr><td>2020</td><td>Wells Fargo (WFC)</td><td>-80%</td><td>Fed stress-test ruling, asset cap pressure</td></tr>
          <tr><td>2022</td><td>AT&amp;T (T)</td><td>-47%</td><td>Warner Media spin-off, FCF coverage below 1x</td></tr>
          <tr><td>2023</td><td>Intel (INTC)</td><td>-66%</td><td>Free cash flow turned negative; foundry investment</td></tr>
          <tr><td>2023</td><td>3M (MMM)</td><td>-55%</td><td>Litigation reserves, FCF compression</td></tr>
          <tr><td>2024</td><td>Walgreens (WBA)</td><td>-48%</td><td>4 yrs of falling FCF, debt rising faster than earnings</td></tr>
          <tr><td>2024</td><td>Intel (INTC)</td><td>Suspended</td><td>Second cut in 18 months</td></tr>
        </tbody>
      </table>

      <h2>The 4 warning signs that show up every time</h2>
      <ol>
        <li>
          <strong>Payout ratio above 80% for two consecutive years.</strong> Kraft
          Heinz, GE, Walgreens, Intel — all stretched here for years before the cut.
          See <Link to="/learn/payout-ratio">the payout ratio guide</Link> for the
          safety bands.
        </li>
        <li>
          <strong>Free cash flow coverage below 1.0x.</strong> When a company can&apos;t
          fund its dividend from operating cash flow, it borrows. Borrowing forever
          to pay the dividend is the most reliable cut precursor in the data.
        </li>
        <li>
          <strong>Debt growing faster than earnings, 3+ years.</strong> A 3M
          (litigation reserves), a Walgreens (acquisitions + Boots integration),
          or an Intel (foundry capex) — all visible on the balance sheet long
          before the cut hit.
        </li>
        <li>
          <strong>Triangle Score declining 2 quarters in a row.</strong> DiviDrip&apos;s
          Triangle bundles payout ratio, FCF coverage, debt-to-equity, and streak
          quality into one number. When it drops faster than peers, that&apos;s the
          alarm.
        </li>
      </ol>

      <h2>Three cases worth dwelling on</h2>

      <h3>GE (2017 + 2018) — the slow-motion collapse</h3>
      <p>
        GE was a dividend Aristocrat for 25+ years. Then the power segment imploded.
        By 2017 the payout ratio was over 100% — a screaming red flag that DiviDrip
        would have flagged today. The first cut went from $0.96 to $0.48 annually
        (-50%). Eighteen months later it went again, to $0.04 annually (-92%). Total
        damage from 2016 to 2018: a 96% dividend reduction.
      </p>
      <p>
        <strong>Lesson:</strong> the second cut is often worse than the first. If
        a company cuts once, the underlying problem rarely resolves quickly.
      </p>

      <h3>AT&amp;T (2022) — the spin-off cut</h3>
      <p>
        AT&amp;T&apos;s 2022 cut wasn&apos;t a failure exactly — it was a restructuring.
        They spun off Warner Media into a separate company (Warner Bros. Discovery),
        and the dividend was right-sized to reflect the smaller remaining telecom
        business. Income investors who held got Warner Bros. Discovery shares as
        compensation. <em>Total</em> return wasn&apos;t the disaster the headline
        suggested.
      </p>
      <p>
        <strong>Lesson:</strong> read WHY the cut happened. A spin-off cut is
        different from a stress cut. The first might be a wash; the second is a
        permanent loss of income.
      </p>

      <h3>Walgreens (2024) — the textbook cut</h3>
      <p>
        Walgreens was a Dividend Aristocrat with 47 consecutive years of raises.
        For four years before the 2024 cut, free cash flow declined while debt
        rose. The payout ratio crossed 90% in 2022. The Triangle Score (had
        DiviDrip been live then) would have been deteriorating for at least 18
        months. The market had partially priced it in — the stock was already
        down 60% from its 2015 peak. The cut just confirmed what the chart was
        already saying.
      </p>
      <p>
        <strong>Lesson:</strong> the longest streaks aren&apos;t immortal. Even
        47-year aristocrats can cut. The streak is a comfort; the metrics underneath
        are the truth.
      </p>

      <h2>How to use this on every new buy</h2>
      <p>
        Before adding any income name to your portfolio, run this 60-second check:
      </p>
      <ol>
        <li>Open the ticker on <Link to="/dashboard">DiviDrip</Link> and check the Dividend Triangle Score. Is it green or red? Trending up or down?</li>
        <li>On the Stock Modal&apos;s Dividend Info tab, look at the payout ratio. Above 80% for an industrial? Above 100% for any sector? Walk away.</li>
        <li>Check FCF coverage of the dividend on the Fundamentals tab. If the company can&apos;t cover from operating cash, it&apos;s borrowing. That&apos;s the GE / KHC / WBA pattern.</li>
        <li>If anything is yellow or red, cross-reference with the <Link to="/learn/dividend-traps">Dividend Trap guide</Link>. You may be looking at the next case study on this page.</li>
      </ol>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Bottom line</h2>
      <p>
        Twelve cuts, one pattern. Stretched payout ratios + falling FCF + rising
        debt + a Triangle Score going the wrong way. Every one of these companies
        flashed those signals before management announced the cut. Most retail
        holders only learned when the news broke.
      </p>
      <p>
        Don&apos;t be most retail holders. Read the metrics before the headline writes itself.
      </p>
    </LearnPage>
  );
}
